import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gauge, ShoppingCart, MapPin, MessageSquare, BadgeCheck, Weight, RefreshCw, Images as ImageIcon } from "lucide-react";
import { formatRON, formatEUR, mediaUrl, PLACEHOLDER_IMG, listingPriceParts, localizedName } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { TrustChips } from "@/components/ListingMatrix";
import OfferButton from "@/components/OfferButton";
import { toast } from "sonner";

const PLACEHOLDER = PLACEHOLDER_IMG;

export default function ProductCard({ product, priority = false }) {
  const { addItem } = useCart();
  const { i18n } = useTranslation();
  const s = product.specs || {};
  const isListing = product.type === "listing";
  const displayName = localizedName(product, i18n.language) || product.name;
  const to = isListing ? `/anunt/${product.id}` : `/produse/${product.id}`;
  const onRequest = isListing ? (product.price_on_request || !product.price) : !product.price;

  return (
    <div className="group border border-slate-200 bg-white flex flex-col hover:border-slate-400 transition-colors" data-testid={`product-card-${product.id}`}>
      <Link to={to} className="block relative overflow-hidden bg-slate-100 aspect-[4/3]">
        <img src={mediaUrl(product.image) || PLACEHOLDER} alt={product.name}
          loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : "auto"}
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {product.images && product.images.length > 1 && (
          <span className="absolute bottom-0 right-0 bg-slate-900/80 text-white text-[10px] font-bold px-2 py-1 flex items-center gap-1" data-testid={`photo-count-${product.id}`}>
            <ImageIcon className="w-3 h-3" /> {product.images.length}
          </span>
        )}
        {isListing ? (
          <span className="absolute top-0 left-0 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5" data-testid="listing-badge">Anunț utilizator</span>
        ) : product.condition === "second-hand" ? (
          <span className="absolute top-0 left-0 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5">Second-hand</span>
        ) : null}
        {product.sponsored && (
          <span className="absolute top-0 right-0 bg-amber-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1" data-testid="sponsored-badge">
            <BadgeCheck className="w-3 h-3" /> Partener oficial
          </span>
        )}
        {product.ai_verified_price && !product.sponsored && (
          <span className="absolute top-0 right-0 bg-amber-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1" data-testid="ai-verified-badge">
            <BadgeCheck className="w-3 h-3" /> AI Verified Price
          </span>
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        {isListing && <div className="mb-2"><TrustChips l={product} /></div>}
        {product.brand && <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600 mb-1">{product.brand}</span>}
        <Link to={to}>
          <h3 className="font-bold text-slate-900 leading-snug mb-2 line-clamp-2 hover:text-orange-600 transition-colors">{displayName}</h3>
        </Link>
        {/* Dense technical chip row — instant metrics for the technologist */}
        {(() => {
          const d = product.dynamic_specs || {};
          const chips = [
            (s.motor_power_kw ?? d.motor_power_kw) != null && { icon: Gauge, v: `${s.motor_power_kw ?? d.motor_power_kw} kW` },
            s.max_rpm != null && { icon: RefreshCw, v: `${s.max_rpm} rpm` },
            s.weight_kg != null && { icon: Weight, v: `${s.weight_kg} kg` },
            d.capacity_m3 != null && { icon: Gauge, v: `${d.capacity_m3} m³` },
            s.year != null && { icon: null, v: s.year },
          ].filter(Boolean);
          return chips.length ? (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`card-specs-${product.id}`}>
              {chips.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-[11px] font-mono px-1.5 py-0.5">
                  {c.icon && <c.icon className="w-3 h-3" />} {c.v}
                </span>
              ))}
            </div>
          ) : null;
        })()}
        {isListing && product.location && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2"><MapPin className="w-3.5 h-3.5" /> {product.location}</div>
        )}
        <div className="mt-auto flex items-end justify-between gap-2 pt-2.5 border-t border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Preț</div>
            {isListing ? (() => { const p = listingPriceParts(product); return (<>
              <div className="text-xl font-black text-slate-900 tracking-tight">{p.main}</div>
              {p.eur ? <div className="text-xs text-slate-400 font-mono">{p.eur}</div> : null}
            </>); })() : (<>
              <div className="text-xl font-black text-slate-900 tracking-tight">{onRequest ? "La cerere" : formatRON(product.price)}</div>
              {product.price_eur ? <div className="text-xs text-slate-400 font-mono">≈ {formatEUR(product.price_eur)}</div> : null}
            </>)}
          </div>
          {isListing ? (
            <Link to={to} className="bg-slate-900 hover:bg-slate-800 text-white p-3 transition-colors" data-testid={`contact-seller-${product.id}`} title="Contactează vânzătorul">
              <MessageSquare className="w-4 h-4" />
            </Link>
          ) : (
            <button onClick={() => { if (!product.price) { toast.info("Preț la cerere — contactați-ne"); return; } addItem(product); toast.success("Adăugat în coș"); }}
              className="bg-orange-600 hover:bg-orange-700 text-white p-3 transition-colors" data-testid={`add-to-cart-${product.id}`}>
              <ShoppingCart className="w-4 h-4" />
            </button>
          )}
        </div>
        {!isListing && <OfferButton product={product} className="mt-3" />}
      </div>
    </div>
  );
}
